import { create } from 'zustand'
import type { ResultsResponse, AIModel, InsightsResponse, DashboardResponse } from '@ekorank/shared'
import {
  ResultsResponseSchema,
  DashboardResponseSchema,
  InsightsResponseSchema,
} from '@ekorank/shared'
import { apiFetch } from '@/lib/api'
import { z } from 'zod'

const ApiDashboardSchema = z.object({
  ok: z.literal(true),
  data: DashboardResponseSchema,
})

const ApiInsightsSchema = z.object({
  ok: z.literal(true),
  data: InsightsResponseSchema,
})

const ApiResultsSchema = z.object({
  ok: z.literal(true),
  data: ResultsResponseSchema,
})

type DashboardData = DashboardResponse
type ResultsData = ResultsResponse
type InsightsData = InsightsResponse

export interface ResultFilters {
  models: AIModel[]
  dateFrom: number | null
  dateTo: number | null
  mentionedOnly: boolean
  promptId: string | null
  page: number
  limit: number
  sort: { col: 'date' | 'model' | 'mentioned' | 'rank'; dir: 'asc' | 'desc' }
}

const defaultFilters = (): ResultFilters => ({
  models: [],
  dateFrom: null,
  dateTo: null,
  mentionedOnly: false,
  promptId: null,
  page: 1,
  limit: 20,
  sort: { col: 'date', dir: 'desc' },
})

/** Build query string from filters for both JSON and CSV endpoints. */
export function buildResultsQuery(filters: ResultFilters): string {
  const params = new URLSearchParams()
  for (const m of filters.models) params.append('model', m)
  if (filters.dateFrom != null) params.set('from', String(filters.dateFrom))
  if (filters.dateTo != null) params.set('to', String(filters.dateTo))
  if (filters.mentionedOnly) params.set('mentioned', 'true')
  if (filters.promptId) params.set('promptId', filters.promptId)
  params.set('page', String(filters.page))
  params.set('limit', String(filters.limit))
  params.set('sort', `${filters.sort.col}:${filters.sort.dir}`)
  return params.toString()
}

interface DashboardState {
  dashboard: DashboardData | null
  insights: InsightsData | null
  results: ResultsData | null
  filters: ResultFilters
  loading: boolean
  error: string | null
  fetchDashboard: () => Promise<void>
  fetchInsights: (brandId: string) => Promise<void>
  fetchResults: (brandId: string) => Promise<void>
  setFilters: (partial: Partial<ResultFilters>, brandId?: string) => void
  resetFilters: (brandId?: string) => void
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  dashboard: null,
  insights: null,
  results: null,
  filters: defaultFilters(),
  loading: false,
  error: null,

  fetchDashboard: async () => {
    set({ loading: true, error: null })
    const result = await apiFetch('/api/dashboard', ApiDashboardSchema)
    if (result.ok) {
      set({ dashboard: result.data.data, loading: false })
    } else {
      set({ error: result.error, loading: false })
    }
  },

  fetchInsights: async (brandId) => {
    const result = await apiFetch(`/api/insights/${brandId}?days=30`, ApiInsightsSchema)
    if (result.ok) {
      set({ insights: result.data.data })
    } else {
      set({ error: result.error })
    }
  },

  fetchResults: async (brandId) => {
    set({ loading: true, error: null })
    const qs = buildResultsQuery(get().filters)
    const result = await apiFetch(`/api/results/${brandId}?${qs}`, ApiResultsSchema)
    if (result.ok) {
      set({ results: result.data.data, loading: false })
    } else {
      set({ error: result.error, loading: false })
    }
  },

  setFilters: (partial, brandId) => {
    // Reset page to 1 if anything other than page itself changed.
    const next: ResultFilters = { ...get().filters, ...partial }
    if (!('page' in partial)) next.page = 1
    set({ filters: next })
    if (brandId) void get().fetchResults(brandId)
  },

  resetFilters: (brandId) => {
    set({ filters: defaultFilters() })
    if (brandId) void get().fetchResults(brandId)
  },
}))
